import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportClientError } from '../lib/telemetry'
import './errorBoundary.css'

// Red de seguridad reactiva (patrón Carpenter). Su caso más probable NO es un bug
// de render, sino un DEPLOY: camp carga chunks en diferido (tiptap, firestore) y si
// alguien tiene la app abierta cuando se publica una versión, el import dinámico
// pide un hash que ya no existe en el servidor y lanza. El aviso de versión nueva
// (UpdateToast) es la vía proactiva; esto es lo que atrapa a quien lo ignoró.
//
// Por eso el botón recarga en vez de reintentar: recargar trae el index nuevo y con
// él el mapa de chunks correcto.

interface Props {
  children: ReactNode
}
interface State {
  failed: boolean
  chunkError: boolean
}

// Un import dinámico fallido se ve así en los navegadores actuales.
function looksLikeStaleChunk(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(msg)
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, chunkError: false }

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, chunkError: looksLikeStaleChunk(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[camp] error no capturado:', error, info.componentStack)
    // El árbol de componentes va junto al stack: sin él, un error en producción
    // dice QUÉ falló pero no DÓNDE, que es lo que hace falta para reproducirlo.
    reportClientError(
      error?.message || String(error),
      (error?.stack ?? '') + (info?.componentStack ? '\n' + info.componentStack : ''),
    )
  }

  private reload = (): void => {
    if (typeof location !== 'undefined') location.reload()
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div className="eb-wrap" role="alert">
        <div className="eb-card">
          <h1 className="eb-title">
            {this.state.chunkError ? 'Se publicó una versión nueva' : 'Algo se atoró'}
          </h1>
          <p className="eb-sub">
            {this.state.chunkError
              ? 'La app cambió mientras la tenías abierta. Recarga para tomar la versión al día.'
              : 'camp tuvo un tropiezo inesperado. Lo que ya se guardó está a salvo; recarga para continuar.'}
          </p>
          <button type="button" className="btn" onClick={this.reload}>
            Recargar
          </button>
        </div>
      </div>
    )
  }
}
